import { Box, Paper, Typography, Chip, Button, Stack, Divider, Badge, IconButton, Tooltip } from '@mui/material';
import { PlayArrow, Visibility, Description } from '@mui/icons-material';
import { WorkflowTask, TaskInstance } from '../../types';

interface TaskCardProps {
  task: WorkflowTask;
  taskInstance?: TaskInstance;
  isLocked: boolean;
  onStart: () => void;
  onViewDetails: () => void;
  onDocumentView?: (folderPath?: string) => void;
  documentCount?: number;
}

export const TaskCard = ({ 
  task, 
  taskInstance, 
  isLocked, 
  onStart, 
  onViewDetails,
  onDocumentView,
  documentCount = 0
}: TaskCardProps) => {
  const getStatusColor = () => {
    if (!taskInstance) {
      if (task.status === 'COMPLETE') return 'success';
      if (task.status === 'CANCELLED') return 'error';
      return 'default';
    }
    switch (taskInstance.status) {
      case 'COMPLETE': return 'success';
      case 'REJECTED': return 'error';
      case 'AWAITING_REVISION': return 'warning';
      case 'AWAITING_APPROVAL': return 'warning';
      case 'IN_PROGRESS': return 'info';
      default: return 'default';
    }
  };

  const getStatusLabel = () => {
    if (taskInstance) {
      return taskInstance.status;
    }
    return task.status;
  };

  const canStart = !isLocked && task.status === 'NOT_STARTED' && !taskInstance;

  return (
    <Paper sx={{ p: 2, mb: 1, position: 'relative' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1">
          TASK {task.displayOrder}: {task.name}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {onDocumentView && (
          <Tooltip title="View documents">
            <Badge
              badgeContent={documentCount}
              color="primary"
              sx={{ mr: 1 }}
            >
              <IconButton
                size="small"
                onClick={() => onDocumentView(`/Phase${task.phaseId || ''}/Task${task.taskId}`)}
                sx={{ cursor: 'pointer' }}
              >
                <Description fontSize="small" />
              </IconButton>
            </Badge>
          </Tooltip>
        )}
        <Chip 
          label={getStatusLabel()}
          color={getStatusColor() as 'success' | 'error' | 'warning' | 'info' | 'default'}
          size="small"
        />
      </Box>

      <Box sx={{ mb: 1 }}>
        <Chip 
          label={task.taskType.replace('_', ' ')}
          size="small"
          variant="outlined"
          sx={{ mr: 1 }}
        />
      </Box>

      {taskInstance && (
        <>
          <Divider sx={{ my: 1 }} />
          <Stack spacing={1}>
            {taskInstance.currentAction && (
              <Typography variant="body2" color="text.secondary">
                Current Action: <strong>{taskInstance.currentAction}</strong>
              </Typography>
            )}
            {taskInstance.revisionEmail && (
              <Typography variant="body2" color="text.secondary">
                Revision Email: {taskInstance.revisionEmail}
              </Typography>
            )}
            {taskInstance.approvalEmail && (
              <Typography variant="body2" color="text.secondary">
                Approval Email: {taskInstance.approvalEmail}
              </Typography>
            )}
            {taskInstance.reviewEmail && (
              <Typography variant="body2" color="text.secondary">
                Review Email: {taskInstance.reviewEmail}
              </Typography>
            )}
            {taskInstance.startedUtc && (
              <Typography variant="body2" color="text.secondary">
                Started: {new Date(taskInstance.startedUtc).toLocaleString()}
              </Typography>
            )}
          </Stack>
        </>
      )}

      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <Button
          variant="contained"
          startIcon={<PlayArrow />}
          onClick={onStart}
          disabled={!canStart}
          size="small"
        >
          Start Task
        </Button>
        {taskInstance && (
          <Button
            variant="outlined"
            startIcon={<Visibility />}
            onClick={onViewDetails}
            size="small"
          >
            View Details
          </Button>
        )}
      </Box>
    </Paper>
  );
};

